import styled, { css } from 'styled-components'
import { Colors, FlexBox, MEDIA } from '@brg/ui'
import { LayoutConst } from 'core/modules/layout.module'

const MEDIA_QUERY = LayoutConst.screen.mac13

const modalScrollThumb = css`

&::-webkit-scrollbar {
    height: 1px;
    width: 0;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background: ${Colors.galBlack5};
  }
  &::-webkit-scrollbar-thumb {
    background: ${Colors.galBlack47};
  }
`

export const Root = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  min-height: calc(100vh - 160px);
  max-height: 100vh;
  padding: 24px 0 48px;
  box-sizing: border-box;
  align-self: flex-start;
  flex-shrink: 1;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  ${modalScrollThumb}

  @media ${MEDIA.MOBILE} {
    align-self: stretch;
    width: 100%;
    max-width: 100%;
    padding: 16px 0 32px;
    min-height: calc(100vh - 120px);
  }
`

export const Header = styled(FlexBox)`
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 40px;
  flex-wrap: wrap;

  @media ${MEDIA.MOBILE} {
    margin-bottom: 24px;
    flex-wrap: nowrap;
    flex-direction: column !important;
    align-items: stretch !important;
    padding: 0 10px;
  }
`

export const HeaderSaveSlot = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  width: 185px;

  @media ${MEDIA.MOBILE} {
    margin-left: 0;
    width: 100%;
  }
`

export const MainTitleWrap = styled.div`
  flex: 1;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    flex: none;
    width: 100%;
  }
`

export const MainTitle = styled.h1`
  margin: 0;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 43px;
  font-weight: 700;
  line-height: 51px;
  color: #151514;
  letter-spacing: -0.02em;

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    font-weight: 700;
    line-height: 25px;
  }
`

export const Body = styled(FlexBox)`
  width: 100%;
  align-items: flex-start;
  gap: 48px;

  @media (max-width: ${MEDIA_QUERY}px) {
    gap: 16px;
    padding: 0 16px;
  }

  @media ${MEDIA.MOBILE} {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 24px;
    padding: 0 10px;
  }
`

export const Sidebar = styled.nav`
  width: 240px;
  flex-shrink: 0;
  border-right: 1px solid #e7e7e7;
  padding-right: 24px;


  @media ${MEDIA.TABLET} {
    width: auto;
    padding-right: 0;
    border-right: none;
  }

  @media ${MEDIA.MOBILE} {
    width: 100%;
    align-self: stretch;
    border-bottom: 1px solid #e7e7e7;
  }
`

export const TabButton = styled.button<{
  $active?: boolean
  $disabled?: boolean
}>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 16px 0 16px 12px;
  margin-left: -12px;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid #e7e7e7;
  background: none;
  font-family: Inter, sans-serif;
  font-size: 20px;
  line-height: 25px;
  color: ${(p) => {
    if (p.$disabled) return '#c4c4c4'
    return p.$active ? '#151514' : '#909090'
  }};
  font-weight: ${(p) => (p.$active && !p.$disabled ? 700 : 600)};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  -webkit-tap-highlight-color: transparent;

  /* Сбрасывает глобальный button:focus из core/starter/styles.css (-webkit-focus-ring-color). */
  &&:focus {
    outline: none;
    box-shadow: none;
  }

  &&:focus-visible {
    outline: 2px solid #c4c4c4;
    outline-offset: 2px;
  }

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    color: ${(p) => (p.$disabled ? '#c4c4c4' : '#151514')};
  }

  &:disabled {
    pointer-events: none;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 15px;
    line-height: 20px;
    padding-top: 12px;
    padding-bottom: 12px;
  }
`

export const MainPanel = styled.div`
  flex: 1;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    width: 100%;
    max-width: 100%;
    align-self: stretch;
  }
`

export const SectionTitle = styled.h2`
  margin: 0 0 24px;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 40px;
  color: #151514;

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    line-height: 25px;
  }
`

export const FieldBlock = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const SettingsExplainFieldBlock = styled(FieldBlock)<{ $muted?: boolean }>`
  ${(p) =>
    p.$muted
      ? `
    opacity: 0.5;
    pointer-events: none;
  `
      : ''}
`

export const SettingsFieldLabel = styled.span<{ $required?: boolean }>`
  display: block;
  margin-bottom: 8px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  color: #909090;

  ${(p) =>
    p.$required &&
    css`
      display: inline-flex;
      align-items: center;
      gap: 8px;

      &::after {
        content: '*';
        color: #c64839;
        font-weight: 600;
        flex-shrink: 0;
      }
    `}
`

export const SettingsInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  border: none;
  border-radius: 3px;
  background: #f2f2f2;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const SettingsSubTitle = styled.h3`
  margin: 0 0 12px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  color: #151514;
`


export const SettingsRadioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const PublishedReadOnlyBanner = styled.div`
  margin: 0 0 22px;
  padding: 0;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.01em;
  color: #c64839;

  @media ${MEDIA.MOBILE} {
    margin-bottom: 18px;
    font-size: 17px;
    line-height: 24px;
  }
`

type TabPanelLockedProps = { $locked?: boolean }

export const TabPanelLocked = styled.div<TabPanelLockedProps>`
  ${(p) =>
    p.$locked
      ? `
    opacity: 0.85;
    pointer-events: none;
    user-select: none;
  `
      : ''}
`
import styled, { css } from 'styled-components'
import { Colors, FlexBox, MEDIA } from '@brg/ui'
import { LayoutConst } from 'core/modules/layout.module'

const MEDIA_QUERY = LayoutConst.screen.mac13

const modalScrollThumb = css`

&::-webkit-scrollbar {
    height: 1px;
    width: 0;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background: ${Colors.galBlack5};
  }
  &::-webkit-scrollbar-thumb {
    background: ${Colors.galBlack47};
  }
`

export const Root = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  min-height: calc(100vh - 160px);
  max-height: 100vh;
  padding: 24px 0 48px;
  box-sizing: border-box;
  align-self: flex-start;
  flex-shrink: 1;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  ${modalScrollThumb}

  @media ${MEDIA.MOBILE} {
    align-self: stretch;
    width: 100%;
    max-width: 100%;
    padding: 16px 0 32px;
    min-height: calc(100vh - 120px);
  }
`

export const Header = styled(FlexBox)`
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 40px;
  flex-wrap: wrap;

  @media ${MEDIA.MOBILE} {
    margin-bottom: 24px;
    flex-wrap: nowrap;
    flex-direction: column !important;
    align-items: stretch !important;
    padding: 0 10px;
  }
`

export const HeaderSaveSlot = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  width: 185px;

  @media ${MEDIA.MOBILE} {
    margin-left: 0;
    width: 100%;
  }
`

export const MainTitleWrap = styled.div`
  flex: 1;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    flex: none;
    width: 100%;
  }
`

export const MainTitle = styled.h1`
  margin: 0;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 43px;
  font-weight: 700;
  line-height: 51px;
  color: #151514;
  letter-spacing: -0.02em;

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    font-weight: 700;
    line-height: 25px;
  }
`

export const Body = styled(FlexBox)`
  width: 100%;
  align-items: flex-start;
  gap: 48px;

  @media (max-width: ${MEDIA_QUERY}px) {
    gap: 16px;
    padding: 0 16px;
  }

  @media ${MEDIA.MOBILE} {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 24px;
    padding: 0 10px;
  }
`

export const Sidebar = styled.nav`
  width: 240px;
  flex-shrink: 0;
  border-right: 1px solid #e7e7e7;
  padding-right: 24px;


  @media ${MEDIA.TABLET} {
    width: auto;
    padding-right: 0;
    border-right: none;
  }

  @media ${MEDIA.MOBILE} {
    width: 100%;
    align-self: stretch;
    border-bottom: 1px solid #e7e7e7;
  }
`

export const TabButton = styled.button<{
  $active?: boolean
  $disabled?: boolean
}>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 16px 0 16px 12px;
  margin-left: -12px;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid #e7e7e7;
  background: none;
  font-family: Inter, sans-serif;
  font-size: 20px;
  line-height: 25px;
  color: ${(p) => {
    if (p.$disabled) return '#c4c4c4'
    return p.$active ? '#151514' : '#909090'
  }};
  font-weight: ${(p) => (p.$active && !p.$disabled ? 700 : 600)};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  -webkit-tap-highlight-color: transparent;

  /* Сбрасывает глобальный button:focus из core/starter/styles.css (-webkit-focus-ring-color). */
  &&:focus {
    outline: none;
    box-shadow: none;
  }

  &&:focus-visible {
    outline: 2px solid #c4c4c4;
    outline-offset: 2px;
  }

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    color: ${(p) => (p.$disabled ? '#c4c4c4' : '#151514')};
  }

  &:disabled {
    pointer-events: none;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 15px;
    line-height: 20px;
    padding-top: 12px;
    padding-bottom: 12px;
  }
`

export const MainPanel = styled.div`
  flex: 1;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    width: 100%;
    max-width: 100%;
    align-self: stretch;
  }
`

export const SectionTitle = styled.h2`
  margin: 0 0 24px;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 40px;
  color: #151514;

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    line-height: 25px;
  }
`

export const FieldBlock = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const SettingsExplainFieldBlock = styled(FieldBlock)<{ $muted?: boolean }>`
  ${(p) =>
    p.$muted
      ? `
    opacity: 0.5;
    pointer-events: none;
  `
      : ''}
`

export const SettingsFieldLabel = styled.span<{ $required?: boolean }>`
  display: block;
  margin-bottom: 8px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  color: #909090;

  ${(p) =>
    p.$required &&
    css`
      display: inline-flex;
      align-items: center;
      gap: 8px;

      &::after {
        content: '*';
        color: #c64839;
        font-weight: 600;
        flex-shrink: 0;
      }
    `}
`

export const SettingsInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  border: none;
  border-radius: 3px;
  background: #f2f2f2;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const SettingsSubTitle = styled.h3`
  margin: 0 0 12px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  color: #151514;
`


export const SettingsRadioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const PublishedReadOnlyBanner = styled.div`
  margin: 0 0 22px;
  padding: 0;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.01em;
  color: #c64839;

  @media ${MEDIA.MOBILE} {
    margin-bottom: 18px;
    font-size: 17px;
    line-height: 24px;
  }
`

type TabPanelLockedProps = { $locked?: boolean }

export const TabPanelLocked = styled.div<TabPanelLockedProps>`
  ${(p) =>
    p.$locked
      ? `
    opacity: 0.85;
    pointer-events: none;
    user-select: none;
  `
      : ''}
`
